import React, { useState, useCallback } from "react";
import ProgressCircle from "../GMBScoring/ProgressCircle";
import "./StoreLocatorScoring.css";
import useLang from "../../i18n/useLang";
import { demoUrl } from "../../i18n/links";
import content from "./content";
import getFallbackObservation from "./getFallbackObservation";

const IMPORTANCE_ORDER = { Haute: 0, Moyenne: 1, Basse: 2 };
const FREE_CRITERIA_COUNT = 5;
const MAX_SW_ITEMS = 5;

/**
 * Render plain text that may contain **bold** markdown fragments.
 * Splits on **...** and wraps matches in <strong>.
 */
const RichText = ({ text }) => {
  if (!text) return null;
  const parts = text.split(/\*\*(.*?)\*\*/g);
  return (
    <>
      {parts.map((part, i) =>
        i % 2 === 1 ? <strong key={i}>{part}</strong> : part,
      )}
    </>
  );
};

// Labels are in content.js (t.sort).
const SORT_MODES = {
  importance: { fn: (a, b) => (IMPORTANCE_ORDER[a.importance] ?? 3) - (IMPORTANCE_ORDER[b.importance] ?? 3) },
  scoreAsc: { fn: (a, b) => a.score - b.score },
  scoreDesc: { fn: (a, b) => b.score - a.score },
};

const StoreLocatorScoring = ({ result, hasFullAccess }) => {
  const lang = useLang();
  const t = content[lang];
  const [sortMode, setSortMode] = useState("importance");

  if (!result) return null;

  const {
    url,
    globalScore,
    summary,
    ogImage,
    timestamp,
    strengths: rawStrengths = [],
    weaknesses: rawWeaknesses = [],
    recommendations = [],
    criteria = [],
    partial = false,
  } = result;

  const strengths = rawStrengths.slice(0, MAX_SW_ITEMS);
  const weaknesses = rawWeaknesses.slice(0, MAX_SW_ITEMS);

  const sortedCriteria = [...criteria].sort(SORT_MODES[sortMode].fn);

  const visibleCriteria = hasFullAccess
    ? sortedCriteria
    : sortedCriteria.slice(0, FREE_CRITERIA_COUNT);

  const lockedCount = sortedCriteria.length - FREE_CRITERIA_COUNT;
  const goodCount = criteria.filter((c) => c.score / c.maxScore >= 0.7).length;
  const badCount = criteria.filter((c) => c.score / c.maxScore < 0.5).length;

  const formattedDate = timestamp
    ? new Date(timestamp).toLocaleDateString(t.dateLocale, { day: "numeric", month: "long", year: "numeric", hour: "2-digit", minute: "2-digit" })
    : null;

  return (
    <div className="sla">
      {formattedDate && (
        <p className="sla-timestamp">{t.auditDate(formattedDate)}</p>
      )}

      {partial && (
        <div className="sla-partial" role="status">
          <svg
            className="sla-partial__icon"
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <circle cx="12" cy="12" r="10" />
            <line x1="12" y1="8" x2="12" y2="12" />
            <line x1="12" y1="16" x2="12.01" y2="16" />
          </svg>
          <div>
            <p className="sla-partial__title">{t.partialTitle}</p>
            <p className="sla-partial__msg">{t.partialMsg}</p>
          </div>
        </div>
      )}

      {/* ── Score ── */}
      <div className="sla-card sla-card--hero">
        <div className="sla-hero">
          <div className="sla-hero__score">
            <ProgressCircle score={globalScore} width={130} />
          </div>
          <div className="sla-hero__body">
            <p className="sla-hero__summary">{summary}</p>
            <div className="sla-hero__meta">
              {ogImage && (
                <OgImagePreview src={ogImage} alt={t.previewAlt(url)} />
              )}
              <div className="sla-hero__meta-text">
                <a
                  className="sla-hero__url"
                  href={url}
                  target="_blank"
                  rel="noreferrer noopener"
                >
                  {url}
                </a>
                <div className="sla-hero__stats">
                  <span className="sla-stat sla-stat--good">
                    {t.goodCount(goodCount)}
                  </span>
                  <span className="sla-stat sla-stat--bad">
                    {t.badCount(badCount)}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ── Aperçu ── */}
      {(strengths.length > 0 || weaknesses.length > 0) && (
        <div className="sla-grid">
          {strengths.length > 0 && (
            <div className="sla-card">
              <div className="sla-card__head">
                <span className="sla-dot sla-dot--green" />
                <h3 className="sla-card__title">{t.strengths}</h3>
              </div>
              <ul className="sla-list">
                {strengths.map((s, i) => (
                  <li key={i}>
                    <RichText text={s} />
                  </li>
                ))}
              </ul>
            </div>
          )}
          {weaknesses.length > 0 && (
            <div className="sla-card">
              <div className="sla-card__head">
                <span className="sla-dot sla-dot--red" />
                <h3 className="sla-card__title">{t.weaknesses}</h3>
              </div>
              <ul className="sla-list">
                {weaknesses.map((w, i) => (
                  <li key={i}>
                    <RichText text={w} />
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}

      {/* ── Recommandations ── */}
      {recommendations.length > 0 && (
        <div className="sla-card">
          <div className="sla-card__head">
            <h3 className="sla-card__title">{t.recommendations}</h3>
          </div>
          <div className="sla-reco">
            {recommendations.map((r, i) => (
              <div key={i} className="sla-reco__item">
                <span className="sla-reco__num">{r.priority || i + 1}</span>
                <p className="sla-reco__text">
                  <RichText text={r.text} />
                </p>
              </div>
            ))}
          </div>

          <div className="sla-demo">
            <div className="sla-demo__content">
              <div className="sla-demo__text">
                <span className="sla-demo__badge">{t.demoBadge}</span>
                <p className="sla-demo__title">{t.demoTitle}</p>
                <p className="sla-demo__desc">{t.demoDesc}</p>
              </div>
              <a
                href={demoUrl(lang, "audit-store-locator")}
                target="_blank"
                rel="noreferrer noopener"
                className="sla-demo__cta"
              >
                {t.demoCta}
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12h14"/><path d="m12 5 7 7-7 7"/></svg>
              </a>
            </div>
          </div>
        </div>
      )}

      {/* ── Critères ── */}
      <div className="sla-card sla-card--flush">
        <div className="sla-card__head sla-card__head--between">
          <h3 className="sla-card__title">{t.criteriaTitle}</h3>
          <div className="sla-sort">
            {Object.keys(SORT_MODES).map((key) => (
              <button
                key={key}
                type="button"
                className={`sla-sort__btn${sortMode === key ? " sla-sort__btn--active" : ""}`}
                onClick={() => setSortMode(key)}
              >
                {t.sort[key]}
              </button>
            ))}
          </div>
        </div>

        <div className="sla-criteria">
          {visibleCriteria.map((c) => (
            <CriterionRow key={c.id} criterion={c} t={t} lang={lang} />
          ))}
        </div>

        {!hasFullAccess && lockedCount > 0 && (
          <div className="sla-gate">
            <p className="sla-gate__text">
              {t.lockedCount(lockedCount)}
            </p>
            <a
              href={demoUrl(lang, "audit-store-locator")}
              target="_blank"
              rel="noreferrer noopener"
              className="sla-gate__cta"
            >
              {t.unlock}
            </a>
          </div>
        )}
      </div>
    </div>
  );
};

const OgImagePreview = ({ src, alt }) => {
  const [failed, setFailed] = useState(false);
  const handleError = useCallback(() => setFailed(true), []);
  if (failed || !src) return null;
  return (
    <div className="sla-hero__og">
      <img src={src} alt={alt} onError={handleError} />
    </div>
  );
};

const CriterionRow = ({ criterion: c, t, lang }) => {
  const [open, setOpen] = useState(false);
  const ratio = c.score / c.maxScore;
  const barColor =
    ratio >= 0.8
      ? "var(--sla-green)"
      : ratio >= 0.5
        ? "var(--sla-orange)"
        : "var(--sla-red)";

  const observation = c.observation || getFallbackObservation(c, lang);
  const recommendation = c.recommendation;
  const hasDetail = observation || recommendation;

  return (
    <div className={`sla-cr${open ? " sla-cr--open" : ""}`}>
      <button
        type="button"
        className="sla-cr__row"
        onClick={() => setOpen((v) => !v)}
      >
        <span className="sla-cr__name">{c.name}</span>
        <span
          className={`sla-cr__imp sla-cr__imp--${c.importance.toLowerCase()}`}
        >
          {t.importance[c.importance] ?? c.importance}
        </span>
        <span className="sla-cr__bar-wrap">
          <span
            className="sla-cr__bar"
            style={{ width: `${ratio * 100}%`, background: barColor }}
          />
        </span>
        <span className="sla-cr__score">{c.score}</span>
        <span className="sla-cr__chevron" />
      </button>

      {open && hasDetail && (
        <div className="sla-cr__detail">
          {observation && (
            <div className="sla-cr__obs">
              <span className="sla-cr__label">{t.observation}</span>
              <RichText text={observation} />
            </div>
          )}
          {recommendation && (
            <div className="sla-cr__rec">
              <span className="sla-cr__label">{t.recommendation}</span>
              <RichText text={recommendation} />
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default StoreLocatorScoring;
